import React, { PropsWithChildren, useMemo } from 'react';
import { CollapsibleTreeItems, SidebarTreeItem } from 'src/components/widgets/sidebar/collapsibleTreeItems';
import { SidebarButtonStyle, SidebarButtons, SidebarListItem } from 'src/components/widgets/sidebar/commonStyleSideBar';
import { TreeSidebarIcon } from 'src/components/widgets/sidebar/treeSideBar';
import styled from 'styled-components';
import { SidebarPlacementItem } from './placementAreaDesigner';
import { SidebarButton, SidebarItemIdType } from '../../widgets/layouts';

interface PlacementSidebarProps<T extends SidebarItemIdType = string> {
    sidebarHeader?: string;
    sidebarHeaderButtons?: SidebarButton[];
    sidebarItems: SidebarPlacementItem<string>[];
    onSidebarItemClick: (item: SidebarPlacementItem<string>) => void;
    onSidebarItemButtonClick?: (button: SidebarButton, item: SidebarPlacementItem<string>) => void;
}

const ListItem = styled(SidebarListItem)`
    min-height: 32px;
    padding: 2px 2px 2px 10px;
    cursor: pointer;
    max-width: none;
`;

const PlacementSidebarContainer = styled.div`
    background-color: white;
    padding: 0px 0px;
    overflow: auto;
`;

const treeItemsStyle = {
    padding: '0'
};

export const PlacementTreeSidebar = <T extends SidebarItemIdType = string>({
    sidebarItems,
    onSidebarItemClick,
    onSidebarItemButtonClick
}: PropsWithChildren<PlacementSidebarProps<T>>) => {
    const items: SidebarTreeItem[] = useMemo(
        () =>
            sidebarItems.map((x) => {
                const firstLevel: SidebarTreeItem[] =
                    x.subItems == null
                        ? []
                        : x.subItems.map((c) => {
                              const secondLevel =
                                  c.subItems == null
                                      ? []
                                      : c.subItems.map((y, i) => {
                                            const element = (
                                                <ListItem key={y.id.toString()} onClick={() => onSidebarItemClick(y)}>
                                                    <span>{y.label}</span>
                                                    <SidebarButtons>
                                                        {y.buttons &&
                                                            y.buttons.map((button) => {
                                                                return (
                                                                    <SidebarButtonStyle
                                                                        key={button.key}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            onSidebarItemButtonClick?.(button, y);
                                                                        }}
                                                                    >
                                                                        <TreeSidebarIcon $isActive={button.isActive} tooltip={button.tooltip}>
                                                                            {button.icon}
                                                                        </TreeSidebarIcon>
                                                                    </SidebarButtonStyle>
                                                                );
                                                            })}
                                                    </SidebarButtons>
                                                </ListItem>
                                            );

                                            return {
                                                id: y?.id,
                                                content: element,
                                                selected: y.selected
                                            };
                                        });

                              let secondNode = (
                                  <ListItem key={c.id.toString()} onClick={() => onSidebarItemClick(c)}>
                                      <span>{c.label}</span>
                                      <SidebarButtons>
                                          {c.buttons &&
                                              c.buttons.map((button) => {
                                                  return (
                                                      <SidebarButtonStyle
                                                          key={button.key}
                                                          onClick={(e) => {
                                                              e.stopPropagation();
                                                              onSidebarItemButtonClick?.(button, c);
                                                          }}
                                                      >
                                                          <TreeSidebarIcon $isActive={button.isActive} tooltip={button.tooltip}>
                                                              {button.icon}
                                                          </TreeSidebarIcon>
                                                      </SidebarButtonStyle>
                                                  );
                                              })}
                                      </SidebarButtons>
                                  </ListItem>
                              );

                              return {
                                  id: c?.id,
                                  content: secondNode,
                                  items: secondLevel,
                                  opened: sidebarItems.length === 1 || c.added || c.subItems?.some((i) => i.added),
                                  notOpenOnClick: false
                              };
                          });

                const firstNode = (
                    <ListItem
                        onClick={(e) => {
                            // e.stopPropagation();
                            onSidebarItemClick(x);
                        }}
                    >
                        <span>{x.label}</span>

                        <SidebarButtons>
                            {x.buttons &&
                                x.buttons.map((button) => {
                                    return (
                                        <SidebarButtonStyle
                                            key={button.key}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSidebarItemButtonClick?.(button, x);
                                            }}
                                        >
                                            <TreeSidebarIcon $isActive={button.isActive} tooltip={button.tooltip}>
                                                {button.icon}
                                            </TreeSidebarIcon>
                                        </SidebarButtonStyle>
                                    );
                                })}
                        </SidebarButtons>
                    </ListItem>
                );

                return {
                    id: x?.id,
                    content: firstNode,
                    items: firstLevel,
                    opened:
                        sidebarItems.length === 1 || x.added || x.subItems?.some((i) => i.added) || x.subItems?.some((i) => i.subItems?.some((y) => y.added)),
                    notOpenOnClick: false
                };
            }),
        [sidebarItems]
    );

    return (
        <PlacementSidebarContainer>
            <CollapsibleTreeItems items={items} customStyle={treeItemsStyle} />
        </PlacementSidebarContainer>
    );
};
